import { Divider, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { FC, useMemo } from "react";
import { useGetCurrentCompanyCache } from "~/queries/useGetCurrentCompany";
import { GetFileByIdResults } from "~/queries/useGetFileById";
import { $d } from "~/utils/currency-display";
import { $toOrderDisplay } from "~/utils/multi-currency";
import { DEFAULT_CURRENCY_CODE, PAYMENT_VERIFICATION_UPLOAD_STAGE } from "~served/configs";
import { bigMath, calculateFile } from "~served/utils";
import { FileInputLike, FileProductInputLike, FileProductOptionInputLike } from "~served/utils/calculations/types";
export interface OrderDetailProps {
  file: GetFileByIdResults;
}
export interface OrderDetailTakePaymentProps {
  id: string;
  display: {
    referenceNumber: string | null | undefined;
    monthlyStatementNumber: string | null | undefined;
    agentName: string | undefined;
    clientName: string | undefined;
    processName: string;
    paymentStage: string;
    orderAmount: number;
    paymentAmounts?: string[];
    balanceAmount: number;
    orderCurrency?: string;
    paymentCurrencies?: string[];
    paymentToOrderCurrXrs?: string[];
    balanceCurrency?: string;
  };
}
export const OrderDetailTable: FC<OrderDetailProps> = ({
  file
}) => {
  const {
    data: company
  } = useGetCurrentCompanyCache();
  const getFilePaymentStage = (file: GetFileByIdResults) => {
    const final = (file._paymentVerifications ?? []).find(p => p.paymentStage === PAYMENT_VERIFICATION_UPLOAD_STAGE.FINAL_PAYMENT);
    if (final) return PAYMENT_VERIFICATION_UPLOAD_STAGE.FINAL_PAYMENT;
    const partial = (file._paymentVerifications ?? []).find(p => p.paymentStage === PAYMENT_VERIFICATION_UPLOAD_STAGE.PARTIAL_PAYMENT);
    if (partial) return PAYMENT_VERIFICATION_UPLOAD_STAGE.PARTIAL_PAYMENT;
    return PAYMENT_VERIFICATION_UPLOAD_STAGE.DEPOSIT_PAYMENT;
  };
  const display = useMemo(() => {
    if (!file) return undefined;
    const paymentCurrencies: string[] = [];
    const paymentAmounts: string[] = [];
    const paymentToOrderCurrXrs: string[] = [];
    let paymentAmountToOrder = 0;
    const totalAmount = calculateFile(file as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>).grossAmount ?? 0;
    for (const payment of file._receipt?.payments ?? []) {
      for (const p of payment.payments) {
        const toOrder = $toOrderDisplay(p.multiCurrencyConfig, file.multiCurrencyConfig);
        paymentCurrencies.push(p.multiCurrencyConfig?.selectedCurrency ?? "N/A");
        paymentAmounts.push($d(p.amount, p.multiCurrencyConfig?.selectedCurrency));
        paymentToOrderCurrXrs.push(toOrder.label);
        paymentAmountToOrder = bigMath.add(paymentAmountToOrder, bigMath.mul(p.amount, toOrder.rate));
      }
    }
    const balanceAmount = bigMath.sub(totalAmount, paymentAmountToOrder);
    return {
      id: file._id,
      display: {
        referenceNumber: file.counterConfigs?.file?.reference,
        monthlyStatementNumber: file.counterConfigs?.monthlyStatement?.reference,
        agentName: file._agent?.name,
        clientName: file._client?.name,
        processName: file._process.name,
        paymentStage: getFilePaymentStage(file),
        orderAmount: totalAmount,
        paymentAmounts: paymentAmounts.length > 0 ? paymentAmounts : undefined,
        balanceAmount: balanceAmount,
        ...(company?.isMultiCurrencyEnabled ? {
          paymentCurrencies: paymentCurrencies.length > 0 ? paymentCurrencies : undefined,
          orderCurrency: file.multiCurrencyConfig?.selectedCurrency ?? DEFAULT_CURRENCY_CODE,
          paymentToOrderCurrXrs: paymentToOrderCurrXrs.length > 0 ? paymentToOrderCurrXrs : undefined,
          balanceCurrency: file.multiCurrencyConfig?.selectedCurrency ?? DEFAULT_CURRENCY_CODE
        } : undefined)
      }
    } as OrderDetailTakePaymentProps;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);
  if (!file || !display) return null;
  return <Grid container spacing={2} data-sentry-element="Grid" data-sentry-component="OrderDetailTable" data-sentry-source-file="order-detail-table.tsx">
			<Grid xs={12} item data-sentry-element="Grid" data-sentry-source-file="order-detail-table.tsx">
				<Divider data-sentry-element="Divider" data-sentry-source-file="order-detail-table.tsx">
					<Typography variant="body2" data-sentry-element="Typography" data-sentry-source-file="order-detail-table.tsx">Order Details</Typography>
				</Divider>
			</Grid>
			<Grid xs={12} item data-sentry-element="Grid" data-sentry-source-file="order-detail-table.tsx">
				<TableContainer component={Paper} sx={{
        width: "100%",
        overflowX: "auto"
      }} data-sentry-element="TableContainer" data-sentry-source-file="order-detail-table.tsx">
					<Table sx={{
          "& td, & th": {
            borderBottom: "none"
          }
        }} data-sentry-element="Table" data-sentry-source-file="order-detail-table.tsx">
						<TableHead data-sentry-element="TableHead" data-sentry-source-file="order-detail-table.tsx">
							<TableRow data-sentry-element="TableRow" data-sentry-source-file="order-detail-table.tsx">
								<TableCell sx={{
                minWidth: 320
              }} data-sentry-element="TableCell" data-sentry-source-file="order-detail-table.tsx">Order #</TableCell>
								<TableCell sx={{
                minWidth: 220
              }} data-sentry-element="TableCell" data-sentry-source-file="order-detail-table.tsx">
									Monthly Statement #
								</TableCell>
								<TableCell sx={{
                minWidth: 180
              }} data-sentry-element="TableCell" data-sentry-source-file="order-detail-table.tsx">Agent</TableCell>
								<TableCell sx={{
                minWidth: 180
              }} data-sentry-element="TableCell" data-sentry-source-file="order-detail-table.tsx">Client</TableCell>
								<TableCell sx={{
                minWidth: 180
              }} data-sentry-element="TableCell" data-sentry-source-file="order-detail-table.tsx">Process</TableCell>
								<TableCell sx={{
                minWidth: 180
              }} data-sentry-element="TableCell" data-sentry-source-file="order-detail-table.tsx">Payment Stage</TableCell>
								<TableCell sx={{
                minWidth: 180
              }} data-sentry-element="TableCell" data-sentry-source-file="order-detail-table.tsx">Order Amount</TableCell>
								{company?.isMultiCurrencyEnabled && <TableCell>Order Currency</TableCell>}
								<TableCell sx={{
                minWidth: 180
              }} data-sentry-element="TableCell" data-sentry-source-file="order-detail-table.tsx">Payment Amount</TableCell>
								{company?.isMultiCurrencyEnabled && <>
										<TableCell>Payment Currency</TableCell>
										<TableCell sx={{
                  minWidth: 220
                }}>
											Payment to Order Curr XR
										</TableCell>
									</>}
								<TableCell sx={{
                minWidth: 180
              }} data-sentry-element="TableCell" data-sentry-source-file="order-detail-table.tsx">Balance Amount</TableCell>
								{company?.isMultiCurrencyEnabled && <TableCell>Balance Currency</TableCell>}
							</TableRow>
						</TableHead>
						<TableBody data-sentry-element="TableBody" data-sentry-source-file="order-detail-table.tsx">
							<TableRow data-sentry-element="TableRow" data-sentry-source-file="order-detail-table.tsx">
								<TableCell data-sentry-element="TableCell" data-sentry-source-file="order-detail-table.tsx">{display.display.referenceNumber} </TableCell>
								<TableCell data-sentry-element="TableCell" data-sentry-source-file="order-detail-table.tsx">{display.display.monthlyStatementNumber}</TableCell>
								<TableCell data-sentry-element="TableCell" data-sentry-source-file="order-detail-table.tsx">{display.display.agentName}</TableCell>
								<TableCell data-sentry-element="TableCell" data-sentry-source-file="order-detail-table.tsx">{display.display.clientName}</TableCell>
								<TableCell data-sentry-element="TableCell" data-sentry-source-file="order-detail-table.tsx">{display.display.processName}</TableCell>
								<TableCell data-sentry-element="TableCell" data-sentry-source-file="order-detail-table.tsx">{display.display.paymentStage}</TableCell>
								<TableCell data-sentry-element="TableCell" data-sentry-source-file="order-detail-table.tsx">
									{$d(display.display.orderAmount, display.display.orderCurrency ?? DEFAULT_CURRENCY_CODE)}
								</TableCell>
								{company?.isMultiCurrencyEnabled && <TableCell>{display.display.orderCurrency}</TableCell>}
								<TableCell data-sentry-element="TableCell" data-sentry-source-file="order-detail-table.tsx">
									{display.display.paymentAmounts ? display.display.paymentAmounts.map((p, i) => <Typography key={`${p}-${i}`}>{p}</Typography>) : ""}
								</TableCell>
								{company?.isMultiCurrencyEnabled && <>
										<TableCell>
											{display.display.paymentCurrencies ? display.display.paymentCurrencies.map((p, i) => <Typography key={`${p}-${i}`}>{p}</Typography>) : ""}
										</TableCell>
										<TableCell>
											{display.display.paymentToOrderCurrXrs ? display.display.paymentToOrderCurrXrs.map((p, i) => <Typography key={`${p}-${i}`}>{p}</Typography>) : ""}
										</TableCell>
									</>}
								<TableCell data-sentry-element="TableCell" data-sentry-source-file="order-detail-table.tsx">
									{$d(display.display.balanceAmount, display.display.balanceCurrency ?? DEFAULT_CURRENCY_CODE)}
								</TableCell>
								{company?.isMultiCurrencyEnabled && <TableCell>{display.display.balanceCurrency}</TableCell>}
							</TableRow>
						</TableBody>
					</Table>
				</TableContainer>
			</Grid>
		</Grid>;
};